import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiProvider } from "@src/store/ApiProvider";
import { pricingActions } from "..";
import { RootState } from "@src/store/store";
import { EventInventoryRecord, getInventoryCacheEventId } from "@src/store/pricing/queryEventInventoryAllSlice";
import { handleError } from "@src/store/getThunkAndSlice";

export interface IUpdateListingPositionBulkParams {
	ListingIds: Array<number>;
	NewSection?: string;
	NewRow?: string;
	SectionLocked?: boolean;
	RowLocked?: boolean;
	SeatLocked?: boolean;
}

export const updateListingPositionBulk = createAsyncThunk<boolean, IUpdateListingPositionBulkParams>(
	"pricing/updateListingPositionBulk",
	async (params, { dispatch, rejectWithValue, getState }) => {
		try {
			const editSection = params.NewSection !== undefined;
			const editRow = params.NewRow !== undefined;

			const body = new URLSearchParams();
			params.ListingIds.forEach((id) => body.append("ListingIds[]", String(id)));
			if (editSection) body.append("NewSection", params.NewSection ?? "");
			if (editRow) {
				if (!editSection) body.append("NewSection", "");
				body.append("NewRow", params.NewRow ?? "");
			}
			if (params.SectionLocked) body.append("SectionLocked", "true");
			if (params.RowLocked) body.append("RowLocked", "true");
			if (params.SeatLocked) body.append("SeatLocked", "true");

			await ApiProvider.default.axios.post(`/api/Pricing/UpdateListingPosition?_dc=${Date.now()}`, body, {
				headers: { "Content-Type": "application/x-www-form-urlencoded" },
			});

			const rootState = getState() as RootState;
			const ids = new Set(params.ListingIds);

			const patch = (item: EventInventoryRecord): EventInventoryRecord => {
				if (!ids.has(item.Listing_ID)) return item;
				return {
					...item,
					...(editSection ? { Section: params.NewSection ?? "" } : {}),
					...(editRow ? { Row: params.NewRow ?? "" } : {}),
					...(params.SectionLocked !== undefined ? { Section_Locked: params.SectionLocked } : {}),
					...(params.RowLocked !== undefined ? { Row_Locked: params.RowLocked } : {}),
					...(params.SeatLocked !== undefined ? { Seat_Locked: params.SeatLocked } : {}),
				};
			};

			const newData = rootState.pricing.eventInventoryAll.data.map(patch);
			const eventId = getInventoryCacheEventId(rootState);
			dispatch(pricingActions.updateInventoryAll({ data: newData, eventId }));

			if (rootState.pricing.globalInventory.data.length) {
				const newGlobalData = (rootState.pricing.globalInventory.data as Array<EventInventoryRecord>).map(patch);
				dispatch(pricingActions.updateGlobalInventory({ data: newGlobalData }));
			}

			return true;
		} catch (error) {
			return rejectWithValue(handleError(error));
		}
	},
);

export const updateListingPositionBulkSlice = createSlice({
	name: "pricing/updateListingPositionBulk",
	initialState: { loading: false, error: null as string | null },
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(updateListingPositionBulk.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateListingPositionBulk.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(updateListingPositionBulk.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) ?? null;
			});
	},
});
